import React from 'react';
import { Progress } from "../components/ui/progress";
import { AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";

export interface FileUploadStatus {
  id: string;
  name: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface UploadProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: FileUploadStatus[];
  totalProgress: number;
}

const UploadProgressModal: React.FC<UploadProgressModalProps> = ({
  isOpen,
  onClose,
  files,
  totalProgress,
}) => {
  // Calculate statistics
  const totalFiles = files.length;
  const completedFiles = files.filter(file => file.status === 'success').length;
  const failedFiles = files.filter(file => file.status === 'error').length;
  const pendingFiles = files.filter(file => ['pending', 'uploading'].includes(file.status)).length;

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Upload Progress</h2>
          <p className="text-sm text-gray-500">
            Uploading {totalFiles} files ({completedFiles} completed, {failedFiles} failed, {pendingFiles} pending)
          </p>
        </div>
        
        {/* Total progress */}
        <div className="mt-4">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium">Total Progress</span>
            <span className="text-sm font-medium">{Math.round(totalProgress)}%</span>
          </div>
          <Progress value={totalProgress} className="h-2" />
        </div>
        
        {/* File list */}
        <ScrollArea className="mt-6" maxHeight="300px">
          <div className="space-y-4">
            {files.map((file) => (
              <div key={file.id} className="border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {file.status === 'success' && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {file.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                    {(file.status === 'pending' || file.status === 'uploading') && (
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                    )}
                    <span className="font-medium truncate max-w-[300px]" title={file.name}>
                      {file.name}
                    </span>
                  </div>
                  <span className="text-xs font-medium">
                    {file.status === 'success' && 'Completed'}
                    {file.status === 'error' && 'Failed'}
                    {file.status === 'pending' && 'Pending'}
                    {file.status === 'uploading' && `${Math.round(file.progress)}%`}
                  </span>
                </div>
                
                {file.status === 'uploading' && (
                  <Progress value={file.progress} className="h-1" />
                )}
                
                {file.status === 'error' && file.error && (
                  <div className="mt-2 text-xs text-red-500">{file.error}</div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default UploadProgressModal;
